import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Formik, Field } from 'formik';
import { Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import ReCAPTCHA from 'react-google-recaptcha';
import { ReactstrapInput } from '../../components/reactstrap-formik';
import { SignUpOTPSchema } from '../../validation/login/login-validation';
import { Common } from '../../utils/constants';
import { getAPIKey, login, signInConfirm } from '../../services/login/login-service';
import images from '../../assets/images';
import { companyNameReplace } from '../../utils';
import { getProfile } from '../../services/user/user-service';
import { AppDispatch, store } from '../../store';
import { changeLanguage, logout, saveUserData, setCurrencyDetails } from '../../store/actions/app.Actions';
import type { ISignInOTPProps } from '../../types/propTypes';
import type { StandardResponse } from '../../models/standard-response';
import type { IUserData } from '../../models/profile';
import type { ISignInOTPFormField } from '../../models/login';
import { maskEmail } from '../../utils/helper';

const SignInOTPForm = (props: ISignInOTPProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [siteKey, setSiteKey] = useState<string>('');
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [showRecaptcha, setShowRecaptcha] = useState<boolean>(false);

  const [captchaSolved, setCaptchaSolved] = useState<boolean>(false);
  const [captchaError, setCaptchaError] = useState<boolean>(false);

  const initialState = {
    code: '',
  };

  useEffect(() => {
    if (showRecaptcha) {
      getRecaptchaSiteKey();
      setCaptchaSolved(false);
      setCaptchaError(true);
    }
  }, [showRecaptcha]);

  const getRecaptchaSiteKey = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    });
    setSiteKey(config.data[0]?.value);
  };

  const handleSubmit = async (value: ISignInOTPFormField) => {
    const downloadVATRedirect = sessionStorage.getItem('vat_receipt_path') || '';

    return await signInConfirm(value)
      .then(async (res) => {
        if (res && res.isSignedIn && res.nextStep.signInStep === Common.AuthenticationSteps.DONE) {
          const authSession = await fetchAuthSession();
          const authResponse = { ...res, authSession };

          getProfile()
            .then((userDetail: StandardResponse<IUserData>) => {
              if (
                userDetail &&
                userDetail.Data &&
                userDetail.Data.status &&
                userDetail.Data.status.code === Common.UserStatus.CANCELLED
              ) {
                dispatch(logout()).then(() => {
                  toast.error(t('login.toast.cancelled'), { autoClose: 15000 });
                });
              } else {
                dispatch(saveUserData({ ...authResponse, ...userDetail.Data }));
                dispatch(changeLanguage(userDetail?.Data?.language_preference));
                dispatch(setCurrencyDetails(userDetail?.Data?.default_currency));
                if (downloadVATRedirect) {
                  sessionStorage.removeItem('vat_receipt_path');
                  navigate(downloadVATRedirect);
                } else if (userDetail && userDetail.Data.terms_and_condition_required) {
                  navigate('/TermsAndCondition', {
                    state: { referralCode: props.referralCode },
                  });
                } else if (userDetail && userDetail.Data.is_profile_verified === false) {
                  navigate('/profile?redirect=true');
                } else if (userDetail && userDetail.Data.is_profile_verified === true) {
                  const marketingConsent = localStorage.getItem(`${Common.MARKETING_CONSENT}-${userDetail.Data.id}`);
                  if (!marketingConsent) {
                    navigate('/profile');
                  } else if (userDetail && userDetail.Data.payment_type === null) {
                    navigate('/payment');
                  } else {
                    navigate('/');
                  }
                }
              }
              moment.locale(store.getState().UserData?.language?.code);
            })
            .catch(() => {
              dispatch(logout());
              toast.error(t('login.toast.error'));
            });
        } else if (
          res &&
          res.isSignedIn === false &&
          res.nextStep.signInStep === Common.AuthenticationSteps.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE
        ) {
          toast.error(t('signup.toast.invalidCode'));
        }
      })
      .catch((error) => {
        if (error && error.name === Common.Authentication.CodeMismatchException) {
          toast.error(t('signup.toast.invalidCode'));
        } else {
          toast.error(t('signup.toast.error'));
        }
      });
  };

  const handleRecaptchaChange = async (recaptchaCode: string | null) => {
    if (recaptchaCode) {
      setCaptchaSolved(true);
      setCaptchaError(false);
      await resendOtpSubmit(recaptchaCode);
      recaptchaRef.current?.reset();
      setSiteKey('');
      setShowRecaptcha(false);
    }
  };

  const handleResendClick = () => {
    if (showRecaptcha && !captchaSolved) {
      setCaptchaError(true);
      return;
    }
    setShowRecaptcha(true);
  };

  const resendOtpSubmit = async (recaptchaCode: string) => {
    document.getElementById('loading')?.classList.remove('spinner-disable');
    await login({
      email: props.email,
      password: props.password,
      recaptchaCode: recaptchaCode,
    })
      .then(() => {
        toast.success(t('signup.toast.resendSignupCode'));
        setShowRecaptcha(false);
      })
      .catch(() => {
        toast.error(t('signup.toast.error'));
      })
      .finally(() => {
        document.getElementById('loading')?.classList.add('spinner-disable');
      });
  };

  return (
    <>
      <Helmet>
        <title>{companyNameReplace(t('login.title'))} </title>
      </Helmet>
      <button
        type="button"
        className="btn btn-icon btn-link pr-0 btn-back"
        onClick={() => props.onChangeStage(Common.AuthStage.SIGNIN)}
        title={t<string>('signup.button.back')}
      >
        <img className="icon" src={images.backArrowRSVG} alt={t<string>('signup.button.back')} />
      </button>
      <div className="title-outer">
        <h1 className="title">
          <span className="icon">
            <img src={images.emailSVG} alt={t<string>('navbar.title')} />
          </span>
          {t('signup.label.otpConfirmationTitle1')}
        </h1>

        <p>
          {t('signup.label.otpConfirmationLoginTitle')}

          <span className="text-primary d-block pt-2">{maskEmail(props.email)}</span>
        </p>
      </div>
      <Formik initialValues={initialState} onSubmit={handleSubmit} validationSchema={SignUpOTPSchema(t)}>
        {(props) => {
          const { handleSubmit } = props;
          return (
            <form className="form-secondary" onSubmit={handleSubmit} autoComplete="off" noValidate>
              <Field type="text" name="code" id="code" label={t('signup.form.code')} component={ReactstrapInput} />
              <Form.Group className="form-group">
                <Button type="submit" className="btn btn-primary w-100">
                  {t('signup.button.submitOtp')}
                </Button>
              </Form.Group>

              <div className="clearfix pt-4">
                <p className="sign-up">
                  {t('signup.label.resendTitle')}{' '}
                  <a href="#" title={t<string>('signup.label.resend')} onClick={handleResendClick}>
                    {t('signup.button.resend')}
                  </a>
                </p>
              </div>

              {showRecaptcha && siteKey && (
                <div className="mt-3 d-flex justify-content-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={siteKey}
                    onChange={handleRecaptchaChange}
                    hl={
                      store?.getState()?.UserData?.language?.code
                        ? store?.getState()?.UserData?.language?.code
                        : store?.getState()?.LanguageInfo?.defaultLanguage?.code
                    }
                    key={
                      store?.getState()?.UserData?.language?.code
                        ? store?.getState()?.UserData?.language?.code
                        : store?.getState()?.LanguageInfo?.defaultLanguage?.code
                    }
                  />
                </div>
              )}
              {captchaError && (
                <p className="text-center mt-2" style={{ color: '#d73a1f', fontSize: '12px' }}>
                  {t('signup.toast.verifyRecaptchaToResend')}
                </p>
              )}
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default SignInOTPForm;
